import { MaruyaApiError, maruyaApi } from './maruya-api'
import type {
  AnswerFeedbackRating,
  AnswerFeedbackResponse,
  AskHistoryTurn,
  CurationListResponse,
  CurationRequest,
  RagAskResponse,
  RagDocumentDetail,
  RagDocumentsResponse,
} from '../types/api'

/** ask は Bedrock 含むため 15〜30 秒想定（設計書 §15.4） */
export const ASK_TIMEOUT_MS = 30_000

export async function askQuestion(
  question: string,
  history: AskHistoryTurn[] = [],
  options?: { signal?: AbortSignal },
): Promise<RagAskResponse> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ASK_TIMEOUT_MS)
  const externalSignal = options?.signal
  const onExternalAbort = () => controller.abort()
  externalSignal?.addEventListener('abort', onExternalAbort)
  try {
    return await maruyaApi<RagAskResponse>('/v1/rag/ask', {
      method: 'POST',
      body: { question, scopes: ['office'], history },
      signal: controller.signal,
    })
  } catch (err) {
    if (isAbortError(err)) {
      if (externalSignal?.aborted) {
        throw err
      }
      throw new MaruyaApiError('回答の取得がタイムアウトしました', 408, {
        error: 'ask_timeout',
        message:
          '回答の取得がタイムアウトしました。しばらくしてから再試行してください。',
      })
    }
    throw err
  } finally {
    clearTimeout(timer)
    externalSignal?.removeEventListener('abort', onExternalAbort)
  }
}

/** CX13: コメント欄の文字数上限。BFF側 rag-feedback.mjs の FEEDBACK_COMMENT_MAX_LENGTH と揃える */
export const FEEDBACK_COMMENT_MAX_LENGTH = 300

/** CX13: 回答フィードバック（👍/👎 + 任意コメント）を送信する */
export async function submitAnswerFeedback(
  traceId: string,
  rating: AnswerFeedbackRating,
  comment?: string,
): Promise<AnswerFeedbackResponse> {
  return maruyaApi<AnswerFeedbackResponse>('/v1/rag/feedback', {
    method: 'POST',
    body: {
      trace_id: traceId,
      rating,
      comment: comment || undefined,
    },
  })
}

export function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  )
}

export async function listDocuments(params: {
  cursor?: string
  limit?: number
} = {}): Promise<RagDocumentsResponse> {
  const search = new URLSearchParams({ scope: 'office' })
  if (params.limit) {
    search.set('limit', String(params.limit))
  }
  if (params.cursor) {
    search.set('cursor', params.cursor)
  }
  return maruyaApi<RagDocumentsResponse>(`/v1/rag/documents?${search}`)
}

export async function getDocument(
  documentId: string,
): Promise<RagDocumentDetail> {
  return maruyaApi<RagDocumentDetail>(
    `/v1/rag/documents/${encodeURIComponent(documentId)}`,
  )
}

export async function createDeleteRequest(
  documentId: string,
  reason: string,
): Promise<CurationRequest> {
  return maruyaApi<CurationRequest>('/v1/rag/curation/requests', {
    method: 'POST',
    body: {
      document_id: documentId,
      request_type: 'delete',
      reason,
    },
  })
}

export async function listCurationRequests(
  status = 'pending',
): Promise<CurationListResponse> {
  const search = new URLSearchParams({ status })
  return maruyaApi<CurationListResponse>(
    `/v1/rag/curation/requests?${search}`,
  )
}

export async function approveCurationRequest(
  requestId: string,
): Promise<CurationRequest> {
  return maruyaApi<CurationRequest>(
    `/v1/rag/curation/requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST' },
  )
}

export async function rejectCurationRequest(
  requestId: string,
  comment = '',
): Promise<CurationRequest> {
  return maruyaApi<CurationRequest>(
    `/v1/rag/curation/requests/${encodeURIComponent(requestId)}/reject`,
    {
      method: 'POST',
      body: comment ? { comment } : {},
    },
  )
}
